import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, XCircle } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { useCountUp } from "@/hooks/useCountUp";
import { useSmoothProgress } from "@/hooks/useSmoothProgress";

interface UploadProgressDialogProps {
  open: boolean;
  totalProducts: number;
  uploadedProducts: number;
  failedProducts: number;
  queuedBatches?: number;
  processedBatches?: number;
  status?: string;
  onCancel?: () => void;
  onClose?: () => void;
  /**
   * Nicht-destruktiver Schliessweg WAEHREND des Laufs (X, Escape, Overlay):
   * blendet nur den Dialog aus, der Upload laeuft serverseitig weiter — exakt
   * das, was die backgroundNote-Copy verspricht. Vorher war der einzige
   * bedienbare Knopf im fokusgefangenen Modal der destruktive Abbruch.
   */
  onDismiss?: () => void;
  cancelling?: boolean;
  /**
   * Fehlergrund aus `upload_jobs.error_message` (Completion-Nachfetch in
   * useUploadJob). Nur im failed-Endzustand gerendert — der Grund lag bisher
   * in der DB und erreichte den Nutzer nie (Konvergenz 6, Befund 3).
   */
  errorMessage?: string | null;
}

export const UploadProgressDialog: React.FC<UploadProgressDialogProps> = ({
  open,
  totalProducts,
  uploadedProducts,
  failedProducts,
  queuedBatches,
  processedBatches,
  status,
  onCancel,
  onClose,
  onDismiss,
  cancelling,
  errorMessage,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  // Nicht-destruktiver Fokus-Anker (Konvergenz 4, Befund 2): Radix wuerde
  // beim Oeffnen das erste fokussierbare Element fokussieren — im laufenden
  // Zustand ist das der destruktive "Upload abbrechen"-Knopf. Der naechste
  // Enter/Space eines Nutzers, der gerade per Tastatur arbeitet, braeche den
  // Upload unbestaetigt ab. Stattdessen landet der Fokus auf dem Titel.
  const titleRef = useRef<HTMLHeadingElement>(null);
  const isCompleted = status === 'completed' || status === 'failed';
  const isFailed = status === 'failed';
  const isProcessing = status === 'processing' || status === 'queued';
  // Die echte Luecke ist `total - uploaded`, NICHT `failed_products`: der
  // Upload-Watchdog (check-job-failures/stale-job-handlers.ts) stempelt einen
  // haengenden Job auf `status='failed'` + `error_message`, fasst den
  // Fehlerzaehler aber nie an. Bei total=500 / uploaded=120 / failed=30 sind
  // real 380 Produkte nicht bei Google, nicht 30 — genau die Zahl, die die
  // Bilanzzeile vorher genannt hat. `failed_products` bleibt im Zahlen-Raster
  // oben sichtbar, dort ist es als eigene Groesse richtig. Math.max schuetzt
  // gegen einen Retry, der den Upload-Zaehler ueber die Jobgroesse treibt.
  const notUploadedProducts = Math.max(0, totalProducts - uploadedProducts);
  const realProgress = totalProducts > 0
    ? Math.round(((uploadedProducts + failedProducts) / totalProducts) * 100)
    : 0;
  const smoothProgress = useSmoothProgress(isProcessing, isCompleted, realProgress);

  const animatedUploaded = useCountUp(uploadedProducts, 600);
  const animatedFailed = useCountUp(failedProducts, 600);

  return (
    // onOpenChange verdrahtet den bisher toten X-Knopf, Escape und den
    // Overlay-Klick: im Endzustand wirkt Schliessen wie der Close-Knopf
    // (onClose), waehrend des Laufs nur als Ausblenden (onDismiss) — der
    // destruktive "Upload abbrechen"-Knopf bleibt der einzige Weg, den Job
    // wirklich zu beenden. `onClose` fuehrt NICHT mehr pauschal zum
    // Erfolgsdialog: useUploadJob haengt den nur noch an `status='completed'`,
    // im failed-Fall schliesst derselbe Weg bloss den Dialog.
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        if (isCompleted) {
          onClose?.();
        } else {
          onDismiss?.();
        }
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          titleRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle ref={titleRef} tabIndex={-1} className="flex items-center gap-2 focus:outline-none">
            {isCompleted ? (
              status === 'completed' ? (
                <CheckCircle2 className="h-5 w-5 text-success" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive" />
              )
            ) : (
              <Spinner size="md" />
            )}
            {isCompleted
              ? status === 'completed' ? t('uploadProgress.titleComplete') : t('uploadProgress.titleFailed')
              : t('uploadProgress.titleUploading')
            }
          </DialogTitle>
          <DialogDescription>
            {queuedBatches && processedBatches !== undefined
              ? t('uploadProgress.batchProgress', { processed: processedBatches.toLocaleString(locale), total: queuedBatches.toLocaleString(locale) })
              : t('uploadProgress.percentComplete', { percent: realProgress })
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <Progress value={smoothProgress} variant="gradient" smooth className="h-2.5 w-full" />
          
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="space-y-1">
              <div className="text-2xl font-bold text-foreground">{totalProducts.toLocaleString(locale)}</div>
              <div className="text-xs text-muted-foreground">{t('uploadProgress.total')}</div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-center gap-1">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <div className="text-2xl font-bold text-success-ink tabular-nums">{animatedUploaded.toLocaleString(locale)}</div>
              </div>
              <div className="text-xs text-muted-foreground">{t('uploadProgress.uploaded')}</div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-center gap-1">
                <XCircle className="h-4 w-4 text-destructive" />
                <div className="text-2xl font-bold text-destructive-ink tabular-nums">{animatedFailed.toLocaleString(locale)}</div>
              </div>
              <div className="text-xs text-muted-foreground">{t('uploadProgress.failed')}</div>
            </div>
          </div>
          
          {/* Konvergenz 6, Befund 3: die Bilanzzeile "Upload abgeschlossen:
              0 Produkte erfolgreich hochgeladen." stand unter dem Titel
              "Upload fehlgeschlagen" — zwei sich widersprechende Aussagen im
              selben Endzustand. Im failed-Fall traegt die Zeile deshalb
              ehrlich BEIDE Seiten: hochgeladen + die echte Luecke aus
              `notUploadedProducts`. Auch beim Teilstand des Watchdogs, dessen
              `failed_products` die Luecke gerade NICHT beschreibt. */}
          <p className="text-sm text-muted-foreground text-center">
            {isFailed
              ? t('uploadProgress.finishedFailed', {
                  count: totalProducts,
                  formatted: totalProducts.toLocaleString(locale),
                  uploadedFormatted: uploadedProducts.toLocaleString(locale),
                  notUploadedFormatted: notUploadedProducts.toLocaleString(locale),
                })
              : isCompleted
                ? t('uploadProgress.finished', { count: uploadedProducts, formatted: uploadedProducts.toLocaleString(locale) })
                : t('uploadProgress.backgroundNote')
            }
          </p>

          {/* Der Fehlergrund liegt in upload_jobs.error_message und ist die
              Antwort auf die Frage, die der Fehlerdialog sonst offen laesst:
              einfach nochmal versuchen, oder erst etwas reparieren? */}
          {isFailed && errorMessage && (
            <p data-testid="upload-error-message" className="text-sm text-destructive-ink text-center break-words">
              {errorMessage}
            </p>
          )}
          {/* Naechste Handlung statt Sackgasse: die Freigaben bleiben
              bestehen, der Upload ist wiederholbar. */}
          {isFailed && (
            <p className="text-xs text-muted-foreground text-center">
              {t('uploadProgress.retryHint')}
            </p>
          )}

          {isCompleted && onClose && (
            <Button onClick={onClose} size="sm" className="w-full">
              {t('common:actions.close')}
            </Button>
          )}
          {isProcessing && onCancel && (
            <Button
              onClick={onCancel}
              variant="destructive"
              size="sm"
              className="w-full"
              disabled={cancelling}
            >
              {cancelling ? <><Spinner size="sm" /> {t('uploadProgress.cancelling')}</> : <><XCircle className="h-4 w-4 mr-2" /> {t('uploadProgress.cancelUpload')}</>}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
